import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatError } from "../lib/api.js";
import { ArrowLeft, Save, Loader2, Settings as SettingsIcon } from "lucide-react";
import PageHeader from "../components/PageHeader.jsx";

const DOC_TYPES_ALL = [
  "Bill of Lading (BL)", "Manifesto", "DU — Documento Único", "Factura comercial",
  "Packing list", "Certificado de origem", "EUR.1",
  "Certificado de inspecção pré-embarque", "Delivery Order", "Outros",
];

const STATUSES_FOR_INSPECTION = [
  "Chegada ao porto", "Descarga concluída", "Recebida no parque Ronil",
  "Em inspecção", "A aguardar PDI", "PDI em curso", "Pronta", "Pronta para VR",
];

export default function LogisticsSettingsPage() {
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");

  useEffect(() => { api.get("/logistics/settings").then(r => setSettings(r.data)).catch(() => setSettings({})); }, []);

  const upd = (k, v) => setSettings({ ...settings, [k]: v });
  const toggleItem = (key, item) => {
    const arr = settings[key] || [];
    const next = arr.includes(item) ? arr.filter(x => x !== item) : [...arr, item];
    upd(key, next);
  };

  const save = async () => {
    setBusy(true); setErr(""); setOk("");
    try {
      const payload = {
        w7_warn_days: Number(settings.w7_warn_days),
        w7_critical_days: Number(settings.w7_critical_days),
        eta_grace_days: Number(settings.eta_grace_days),
        mandatory_doc_types: settings.mandatory_doc_types,
        alert_notify_owner: !!settings.alert_notify_owner,
        alert_notify_manager: !!settings.alert_notify_manager,
        alert_notify_hq: !!settings.alert_notify_hq,
        require_inspection_statuses: settings.require_inspection_statuses,
      };
      const r = await api.patch("/logistics/settings", payload);
      setSettings(r.data);
      setOk("Configurações guardadas.");
    } catch (e) { setErr(formatError(e.response?.data?.detail) || "Falha a guardar"); }
    finally { setBusy(false); }
  };

  const runScan = async () => {
    setBusy(true); setErr(""); setOk("");
    try {
      const r = await api.post("/logistics/alerts/scan");
      setOk(`Scan concluído: ${JSON.stringify(r.data.counts)}`);
    } catch (e) { setErr(formatError(e.response?.data?.detail) || "Falha no scan"); }
    finally { setBusy(false); }
  };

  if (!settings) return <div className="p-8 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> A carregar…</div>;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-screen-lg mx-auto">
      <Link to="/logistica" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-ronil-navy mb-3"><ArrowLeft className="w-3.5 h-3.5" /> Logística</Link>
      <PageHeader
        overline="Administração"
        title="Configurações da Logística"
        subtitle="Ajusta os limites de alertas, documentos obrigatórios e destinatários das notificações."
      />

      <div className="space-y-6">
        <section className="bg-white border border-slate-200 rounded-md p-5">
          <h3 className="font-heading font-bold text-ronil-navy text-sm uppercase tracking-widest mb-3">Limites de alertas</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <NumberField label="Aviso W7 (dias)" value={settings.w7_warn_days} onChange={(v) => upd("w7_warn_days", v)} testid="w7-warn" />
            <NumberField label="Crítico W7 (dias)" value={settings.w7_critical_days} onChange={(v) => upd("w7_critical_days", v)} testid="w7-crit" />
            <NumberField label="Tolerância ETA (dias)" value={settings.eta_grace_days} onChange={(v) => upd("eta_grace_days", v)} testid="eta-grace" />
          </div>
        </section>

        <section className="bg-white border border-slate-200 rounded-md p-5">
          <h3 className="font-heading font-bold text-ronil-navy text-sm uppercase tracking-widest mb-3">Documentos obrigatórios</h3>
          <p className="text-xs text-slate-500 mb-2">Após a chegada da viatura, o sistema avisa se faltar qualquer um destes.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-1 text-sm">
            {DOC_TYPES_ALL.map(t => (
              <label key={t} className="inline-flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={(settings.mandatory_doc_types || []).includes(t)} onChange={() => toggleItem("mandatory_doc_types", t)} />
                {t}
              </label>
            ))}
          </div>
        </section>

        <section className="bg-white border border-slate-200 rounded-md p-5">
          <h3 className="font-heading font-bold text-ronil-navy text-sm uppercase tracking-widest mb-3">Estados que exigem inspecção</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-1 text-sm">
            {STATUSES_FOR_INSPECTION.map(s => (
              <label key={s} className="inline-flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={(settings.require_inspection_statuses || []).includes(s)} onChange={() => toggleItem("require_inspection_statuses", s)} />
                {s}
              </label>
            ))}
          </div>
        </section>

        <section className="bg-white border border-slate-200 rounded-md p-5">
          <h3 className="font-heading font-bold text-ronil-navy text-sm uppercase tracking-widest mb-3">Destinatários dos alertas</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
            <label className="inline-flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={!!settings.alert_notify_owner} onChange={(e) => upd("alert_notify_owner", e.target.checked)} data-testid="notify-owner" /> Responsável do VIN</label>
            <label className="inline-flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={!!settings.alert_notify_manager} onChange={(e) => upd("alert_notify_manager", e.target.checked)} data-testid="notify-manager" /> Gestor do dealer</label>
            <label className="inline-flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={!!settings.alert_notify_hq} onChange={(e) => upd("alert_notify_hq", e.target.checked)} data-testid="notify-hq" /> HQ Maputo</label>
          </div>
        </section>

        {err && <div className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded px-3 py-2">{err}</div>}
        {ok && <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-3 py-2">{ok}</div>}

        <div className="flex items-center justify-between gap-3">
          <button onClick={runScan} disabled={busy} className="bg-amber-50 border border-amber-200 text-amber-800 hover:bg-amber-100 rounded-md px-3 py-2 text-sm font-heading font-semibold" data-testid="run-scan-btn">
            Correr scanner agora
          </button>
          <button onClick={save} disabled={busy} className="bg-ronil-navy text-white hover:bg-ronil-navy-hover rounded-md px-4 py-2 text-sm font-heading font-semibold inline-flex items-center gap-2 disabled:opacity-50" data-testid="save-settings-btn">
            <Save className="w-4 h-4" /> {busy ? "A guardar…" : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}

function NumberField({ label, value, onChange, testid }) {
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="text-[10px] uppercase tracking-widest font-heading font-semibold text-slate-500">{label}</span>
      <input type="number" min="0" value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="w-full border border-slate-200 rounded-md px-2.5 py-1.5 text-sm focus:border-ronil-navy focus:outline-none focus:ring-2 focus:ring-ronil-navy/10" data-testid={testid} />
    </label>
  );
}
